// Store singleton del inventario de SharePoint.
//
// Responsabilidades:
//   - Cargar el inventario UNA VEZ por sesión y compartirlo entre vistas
//     (HomeView, InventoryView, futuras) vía pub/sub.
//   - Cachear el snapshot enriquecido en IndexedDB con TTL de 10 min
//     para render instantáneo al recargar la pestaña.
//   - Permitir refresh manual desde el header.
//
// Estrategia (stale-while-revalidate):
//   - Hay cache fresca (<10 min) → render inmediato del cache + fetch en
//     background con stale=true.
//   - No hay cache o expirada → fetch bloqueante con state='loading'.
//   - Refresh manual → fetch que sobrescribe cache, mantiene data previa
//     visible mientras tanto (stale=true) o muestra spinner en el botón.

import { getDb, STORE_INVENTORY_CACHE } from './db.js'
import { listProjectInventory } from '../graph/sharepoint-reader.js'
import { parseDocumentName } from './doc-parser.js'

export const CACHE_TTL_MS = 10 * 60 * 1000
const CACHE_KEY = 'main'

let state = {
  state: 'idle', // idle | loading | ready | error
  data: null, // { files (enriched), folders, siteId, scannedAt }
  error: null,
  scannedAt: null,
  stale: false,
  progress: { scanned: 0, queueSize: null }
}

let inflight = null
const listeners = new Set()

function emit() {
  const snapshot = getInventoryState()
  for (const cb of listeners) {
    try {
      cb(snapshot)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[inventory-store] listener falló:', err)
    }
  }
}

export function subscribeInventoryChange(callback) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

export function getInventoryState() {
  return { ...state }
}

export function getInventory() {
  return state.data
}

async function loadFromIDB() {
  try {
    const db = await getDb()
    return await db.get(STORE_INVENTORY_CACHE, CACHE_KEY)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[inventory-store] No se pudo leer IDB:', err)
    return null
  }
}

async function saveToIDB(data) {
  try {
    const db = await getDb()
    await db.put(
      STORE_INVENTORY_CACHE,
      { data, scannedAt: data.scannedAt },
      CACHE_KEY
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[inventory-store] No se pudo guardar IDB:', err)
  }
}

function enrichFiles(files) {
  return files.map((f) => ({
    ...f,
    metadata: parseDocumentName(f.name, f.relativePath)
  }))
}

async function performFetch() {
  const raw = await listProjectInventory({
    onProgress: (n, q) => {
      // Reporta progreso solo si estamos en estado 'loading'; durante una
      // revalidación silenciosa (state ya 'ready'), no inflamos updates.
      if (state.state === 'loading') {
        state = {
          ...state,
          progress: { scanned: n, queueSize: q }
        }
        emit()
      }
    }
  })
  const enriched = {
    ...raw,
    files: enrichFiles(raw.files)
  }
  state = {
    state: 'ready',
    data: enriched,
    error: null,
    scannedAt: enriched.scannedAt,
    stale: false,
    progress: { scanned: enriched.files.length, queueSize: 0 }
  }
  await saveToIDB(enriched)
  emit()
  return enriched
}

/**
 * Carga el inventario si aún no está disponible. Idempotente: múltiples
 * llamadas simultáneas comparten la misma promesa en curso.
 */
export async function initInventory() {
  if (state.state === 'ready' && !state.stale) return state.data
  if (inflight) return inflight

  // Intentar IDB primero.
  const stored = await loadFromIDB()
  const now = Date.now()
  if (stored?.data && stored?.scannedAt) {
    const ageMs = now - new Date(stored.scannedAt).getTime()
    if (ageMs < CACHE_TTL_MS) {
      // Cache fresca: render instantáneo. Si los archivos no tienen
      // metadata enriquecida (formato viejo o por seguridad), enriquecer.
      const cachedData = {
        ...stored.data,
        files: stored.data.files?.[0]?.metadata
          ? stored.data.files
          : enrichFiles(stored.data.files || [])
      }
      state = {
        state: 'ready',
        data: cachedData,
        error: null,
        scannedAt: stored.scannedAt,
        stale: true,
        progress: {
          scanned: cachedData.files.length,
          queueSize: 0
        }
      }
      emit()
      // Revalidación silenciosa en background.
      inflight = performFetch()
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn(
            '[inventory-store] Revalidación silenciosa falló:',
            err?.message || err
          )
          state = { ...state, stale: false }
          emit()
          return null
        })
        .finally(() => {
          inflight = null
        })
      return cachedData
    }
  }

  // Cache fría o ausente: bloqueante.
  state = {
    state: 'loading',
    data: null,
    error: null,
    scannedAt: null,
    stale: false,
    progress: { scanned: 0, queueSize: null }
  }
  emit()

  inflight = (async () => {
    try {
      return await performFetch()
    } catch (err) {
      state = {
        state: 'error',
        data: null,
        error: err?.message || String(err),
        scannedAt: null,
        stale: false,
        progress: { scanned: 0, queueSize: null }
      }
      emit()
      throw err
    } finally {
      inflight = null
    }
  })()
  return inflight
}

/**
 * Refresh manual: vuelve a hacer fetch de SharePoint sin importar el
 * estado de la cache. Mantiene los datos previos visibles (stale=true)
 * mientras dura el nuevo fetch.
 */
export async function refreshInventory() {
  if (inflight) return inflight
  if (state.data) {
    state = { ...state, stale: true }
    emit()
  } else {
    state = {
      ...state,
      state: 'loading',
      progress: { scanned: 0, queueSize: null }
    }
    emit()
  }

  inflight = (async () => {
    try {
      return await performFetch()
    } catch (err) {
      state = {
        ...state,
        stale: false,
        error: err?.message || String(err)
      }
      emit()
      throw err
    } finally {
      inflight = null
    }
  })()
  return inflight
}
